import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../config';
import AdminLayout from './AdminLayout';
import './Admin.css';

function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { currentUser } = useAuth();

    useEffect(() => {
        fetchDashboardStats();
    }, []);

    const getPlaceholderStats = () => ({
        users: { total: 0, newToday: 0, activeToday: 0, activeWeek: 0 },
        subscriptions: { premium: 0, free: 0 },
        revenue: { mrr: 0 },
        content: { topicsGenerated: 0, lessonsCompleted: 0 }
    });

    const fetchDashboardStats = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!currentUser) {
                throw new Error('Not authenticated');
            }

            const token = await currentUser.getIdToken();
            const response = await fetch(`${API_URL}/api/admin/analytics/overview`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Server error: ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
                setStats(data.analytics);
            } else {
                setStats(getPlaceholderStats());
            }
        } catch (err) {
            console.error('Dashboard stats error:', err);
            setError(err.message);
            setStats(getPlaceholderStats());
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => {
        if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
        return num?.toString() || '0';
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="admin-loading-container">
                    <div className="spinner"></div>
                    <p>Loading dashboard...</p>
                </div>
            </AdminLayout>
        );
    }

    if (error) {
        return (
            <AdminLayout>
                <div className="admin-error">
                    <h2>Error Loading Dashboard</h2>
                    <p>{error}</p>
                    <button onClick={fetchDashboardStats}>Retry</button>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="admin-dashboard">
                <div className="admin-header">
                    <h1>Dashboard Overview</h1>
                    <button className="refresh-btn" onClick={fetchDashboardStats}>
                        🔄 Refresh
                    </button>
                </div>

                {/* Key Metrics */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon users">👥</div>
                        <div className="stat-content">
                            <span className="stat-value">{formatNumber(stats?.users?.total)}</span>
                            <span className="stat-label">Total Users</span>
                            <span className="stat-change positive">
                                +{stats?.users?.newToday || 0} today
                            </span>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon revenue">💰</div>
                        <div className="stat-content">
                            <span className="stat-value">{formatCurrency(stats?.revenue?.mrr)}</span>
                            <span className="stat-label">Monthly Revenue</span>
                            <span className="stat-change positive">
                                {formatCurrency(stats?.revenue?.today)} today
                            </span>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon subscriptions">⭐</div>
                        <div className="stat-content">
                            <span className="stat-value">{stats?.subscriptions?.premium || 0}</span>
                            <span className="stat-label">Premium Users</span>
                            <span className="stat-sublabel">
                                {stats?.subscriptions?.free || 0} free users
                            </span>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="stat-icon topics">📚</div>
                        <div className="stat-content">
                            <span className="stat-value">{formatNumber(stats?.content?.topicsGenerated)}</span>
                            <span className="stat-label">Topics Generated</span>
                            <span className="stat-sublabel">
                                {stats?.content?.lessonsCompleted || 0} lessons completed
                            </span>
                        </div>
                    </div>
                </div>

                {/* Secondary Stats */}
                <div className="stats-row">
                    <div className="stat-card-wide">
                        <h3>User Activity</h3>
                        <div className="activity-stats">
                            <div className="activity-item">
                                <span className="activity-value">{stats?.users?.activeToday || 0}</span>
                                <span className="activity-label">Active Today</span>
                            </div>
                            <div className="activity-item">
                                <span className="activity-value">{stats?.users?.activeWeek || 0}</span>
                                <span className="activity-label">Active This Week</span>
                            </div>
                            <div className="activity-item">
                                <span className="activity-value">{stats?.users?.activeMonth || 0}</span>
                                <span className="activity-label">Active This Month</span>
                            </div>
                        </div>
                    </div>

                    <div className="stat-card-wide">
                        <h3>Content Stats</h3>
                        <div className="activity-stats">
                            <div className="activity-item">
                                <span className="activity-value">{stats?.content?.quizzesTaken || 0}</span>
                                <span className="activity-label">Quizzes Taken</span>
                            </div>
                            <div className="activity-item">
                                <span className="activity-value">{stats?.content?.flashcardsStudied || 0}</span>
                                <span className="activity-label">Flashcards Studied</span>
                            </div>
                            <div className="activity-item">
                                <span className="activity-value">{stats?.content?.avgSessionTime || '0m'}</span>
                                <span className="activity-label">Avg Session Time</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* System Health */}
                <div className="system-health-section">
                    <h3>System Health</h3>
                    <div className="health-grid">
                        <div className="health-item">
                            <span className="health-indicator healthy"></span>
                            <span>API Status</span>
                        </div>
                        <div className="health-item">
                            <span className="health-indicator healthy"></span>
                            <span>Database</span>
                        </div>
                        <div className="health-item">
                            <span className="health-indicator healthy"></span>
                            <span>AI Services</span>
                        </div>
                        <div className="health-item">
                            <span className="health-indicator healthy"></span>
                            <span>Payments</span>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="quick-actions-section">
                    <h3>Quick Actions</h3>
                    <div className="quick-actions-grid">
                        <button className="quick-action-btn" onClick={() => window.location.href = '/admin/users'}>
                            <span>👥</span>
                            Manage Users
                        </button>
                        <button className="quick-action-btn" onClick={() => window.location.href = '/admin/content'}>
                            <span>📝</span>
                            Review Content
                        </button>
                        <button className="quick-action-btn" onClick={() => window.location.href = '/admin/topics'}>
                            <span>🏷️</span>
                            Manage Topics
                        </button>
                        <button className="quick-action-btn" onClick={() => window.location.href = '/admin/logs'}>
                            <span>📋</span>
                            View Logs
                        </button>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}

export default AdminDashboard;
